import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files - English
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enAssociates from './locales/en/associates.json';
import enInterviews from './locales/en/interviews.json';
import enSettings from './locales/en/settings.json';
import enNavigation from './locales/en/navigation.json';
import enErrors from './locales/en/errors.json';
import enForms from './locales/en/forms.json';
import enAccessibility from './locales/en/accessibility.json';
import enQuestions from './locales/en/questions.json';
import enReviewQueue from './locales/en/reviewQueue.json';
import enAssociateProfile from './locales/en/associateProfile.json';
import enTemplates from './locales/en/templates.json';
import enIqQuestions from './locales/en/iqQuestions.json';
import enAnalytics from './locales/en/analytics.json';
import enAuditLogs from './locales/en/auditLogs.json';

// Spanish
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esAssociates from './locales/es/associates.json';
import esInterviews from './locales/es/interviews.json';
import esSettings from './locales/es/settings.json';
import esNavigation from './locales/es/navigation.json';
import esErrors from './locales/es/errors.json';
import esForms from './locales/es/forms.json';
import esAccessibility from './locales/es/accessibility.json';

// French
import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frDashboard from './locales/fr/dashboard.json';
import frAssociates from './locales/fr/associates.json';
import frInterviews from './locales/fr/interviews.json';
import frSettings from './locales/fr/settings.json';
import frNavigation from './locales/fr/navigation.json';
import frErrors from './locales/fr/errors.json';
import frForms from './locales/fr/forms.json';
import frAccessibility from './locales/fr/accessibility.json';

// German
import deCommon from './locales/de/common.json';
import deAuth from './locales/de/auth.json';
import deDashboard from './locales/de/dashboard.json';
import deAssociates from './locales/de/associates.json';
import deInterviews from './locales/de/interviews.json';
import deSettings from './locales/de/settings.json';
import deNavigation from './locales/de/navigation.json';
import deErrors from './locales/de/errors.json';
import deForms from './locales/de/forms.json';
import deAccessibility from './locales/de/accessibility.json';

// Portuguese
import ptCommon from './locales/pt/common.json';
import ptAuth from './locales/pt/auth.json';
import ptDashboard from './locales/pt/dashboard.json';
import ptAssociates from './locales/pt/associates.json';
import ptInterviews from './locales/pt/interviews.json';
import ptSettings from './locales/pt/settings.json';
import ptNavigation from './locales/pt/navigation.json';
import ptErrors from './locales/pt/errors.json';
import ptForms from './locales/pt/forms.json';
import ptAccessibility from './locales/pt/accessibility.json';

// Italian
import itCommon from './locales/it/common.json';
import itAuth from './locales/it/auth.json';
import itDashboard from './locales/it/dashboard.json';
import itAssociates from './locales/it/associates.json';
import itInterviews from './locales/it/interviews.json';
import itSettings from './locales/it/settings.json';

// Dutch
import nlCommon from './locales/nl/common.json';
import nlAuth from './locales/nl/auth.json';
import nlDashboard from './locales/nl/dashboard.json';
import nlAssociates from './locales/nl/associates.json';
import nlInterviews from './locales/nl/interviews.json';
import nlSettings from './locales/nl/settings.json';

// Japanese
import jaCommon from './locales/ja/common.json';
import jaAuth from './locales/ja/auth.json';
import jaDashboard from './locales/ja/dashboard.json';
import jaAssociates from './locales/ja/associates.json';
import jaInterviews from './locales/ja/interviews.json';
import jaSettings from './locales/ja/settings.json';

// Chinese Simplified
import zhCNCommon from './locales/zh-CN/common.json';
import zhCNAuth from './locales/zh-CN/auth.json';
import zhCNDashboard from './locales/zh-CN/dashboard.json';
import zhCNAssociates from './locales/zh-CN/associates.json';
import zhCNInterviews from './locales/zh-CN/interviews.json';
import zhCNSettings from './locales/zh-CN/settings.json';

// Chinese Traditional
import zhTWCommon from './locales/zh-TW/common.json';
import zhTWAuth from './locales/zh-TW/auth.json';
import zhTWDashboard from './locales/zh-TW/dashboard.json';
import zhTWAssociates from './locales/zh-TW/associates.json';
import zhTWInterviews from './locales/zh-TW/interviews.json';
import zhTWSettings from './locales/zh-TW/settings.json';

// Korean
import koCommon from './locales/ko/common.json';
import koAuth from './locales/ko/auth.json';
import koDashboard from './locales/ko/dashboard.json';
import koAssociates from './locales/ko/associates.json';
import koInterviews from './locales/ko/interviews.json';
import koSettings from './locales/ko/settings.json';

// Arabic
import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arDashboard from './locales/ar/dashboard.json';
import arAssociates from './locales/ar/associates.json';
import arInterviews from './locales/ar/interviews.json';
import arSettings from './locales/ar/settings.json';

// ============================================================================
// LANGUAGE CONFIGURATION
// ============================================================================

/**
 * Available languages with their display names, flags, and text direction
 */
export const LANGUAGES = {
  en: { name: 'English', nativeName: 'English', flag: 'US', dir: 'ltr', locale: 'en-US' },
  es: { name: 'Spanish', nativeName: 'Espanol', flag: 'ES', dir: 'ltr', locale: 'es-ES' },
  fr: { name: 'French', nativeName: 'Francais', flag: 'FR', dir: 'ltr', locale: 'fr-FR' },
  de: { name: 'German', nativeName: 'Deutsch', flag: 'DE', dir: 'ltr', locale: 'de-DE' },
  pt: { name: 'Portuguese', nativeName: 'Portugues', flag: 'PT', dir: 'ltr', locale: 'pt-PT' },
  it: { name: 'Italian', nativeName: 'Italiano', flag: 'IT', dir: 'ltr', locale: 'it-IT' },
  nl: { name: 'Dutch', nativeName: 'Nederlands', flag: 'NL', dir: 'ltr', locale: 'nl-NL' },
  ja: { name: 'Japanese', nativeName: 'Nihongo', flag: 'JP', dir: 'ltr', locale: 'ja-JP' },
  'zh-CN': { name: 'Chinese (Simplified)', nativeName: 'Zhongwen', flag: 'CN', dir: 'ltr', locale: 'zh-CN' },
  'zh-TW': { name: 'Chinese (Traditional)', nativeName: 'Zhongwen', flag: 'TW', dir: 'ltr', locale: 'zh-TW' },
  ko: { name: 'Korean', nativeName: 'Hangugeo', flag: 'KR', dir: 'ltr', locale: 'ko-KR' },
  ar: { name: 'Arabic', nativeName: 'Arabiy', flag: 'SA', dir: 'rtl', locale: 'ar-SA' },
} as const;

export type Language = keyof typeof LANGUAGES;

/**
 * List of RTL (Right-to-Left) languages
 */
export const RTL_LANGUAGES: Language[] = ['ar'];

/**
 * Check if a language is RTL
 */
export const isRTL = (language: Language): boolean => {
  return LANGUAGES[language]?.dir === 'rtl';
};

/**
 * Get the text direction for a language
 */
export const getDirection = (language: Language): 'ltr' | 'rtl' => {
  return LANGUAGES[language]?.dir || 'ltr';
};

/**
 * Get the locale code for a language
 */
export const getLocale = (language: Language): string => {
  return LANGUAGES[language]?.locale || 'en-US';
};

/**
 * Available translation namespaces
 */
export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'associates',
  'interviews',
  'settings',
  'navigation',
  'errors',
  'forms',
  'accessibility',
  'questions',
  'reviewQueue',
  'associateProfile',
  'templates',
  'iqQuestions',
  'analytics',
  'auditLogs',
] as const;

export type TranslationNamespace = typeof NAMESPACES[number];

// ============================================================================
// TRANSLATION RESOURCES
// ============================================================================

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    associates: enAssociates,
    interviews: enInterviews,
    settings: enSettings,
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    dashboard: esDashboard,
    associates: esAssociates,
    interviews: esInterviews,
    settings: esSettings,
    navigation: esNavigation,
    errors: esErrors,
    forms: esForms,
    accessibility: esAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    dashboard: frDashboard,
    associates: frAssociates,
    interviews: frInterviews,
    settings: frSettings,
    navigation: frNavigation,
    errors: frErrors,
    forms: frForms,
    accessibility: frAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  de: {
    common: deCommon,
    auth: deAuth,
    dashboard: deDashboard,
    associates: deAssociates,
    interviews: deInterviews,
    settings: deSettings,
    navigation: deNavigation,
    errors: deErrors,
    forms: deForms,
    accessibility: deAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  pt: {
    common: ptCommon,
    auth: ptAuth,
    dashboard: ptDashboard,
    associates: ptAssociates,
    interviews: ptInterviews,
    settings: ptSettings,
    navigation: ptNavigation,
    errors: ptErrors,
    forms: ptForms,
    accessibility: ptAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  it: {
    common: itCommon,
    auth: itAuth,
    dashboard: itDashboard,
    associates: itAssociates,
    interviews: itInterviews,
    settings: itSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  nl: {
    common: nlCommon,
    auth: nlAuth,
    dashboard: nlDashboard,
    associates: nlAssociates,
    interviews: nlInterviews,
    settings: nlSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  ja: {
    common: jaCommon,
    auth: jaAuth,
    dashboard: jaDashboard,
    associates: jaAssociates,
    interviews: jaInterviews,
    settings: jaSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  'zh-CN': {
    common: zhCNCommon,
    auth: zhCNAuth,
    dashboard: zhCNDashboard,
    associates: zhCNAssociates,
    interviews: zhCNInterviews,
    settings: zhCNSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  'zh-TW': {
    common: zhTWCommon,
    auth: zhTWAuth,
    dashboard: zhTWDashboard,
    associates: zhTWAssociates,
    interviews: zhTWInterviews,
    settings: zhTWSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  ko: {
    common: koCommon,
    auth: koAuth,
    dashboard: koDashboard,
    associates: koAssociates,
    interviews: koInterviews,
    settings: koSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
  ar: {
    common: arCommon,
    auth: arAuth,
    dashboard: arDashboard,
    associates: arAssociates,
    interviews: arInterviews,
    settings: arSettings,
    // Using English fallback for new namespaces
    navigation: enNavigation,
    errors: enErrors,
    forms: enForms,
    accessibility: enAccessibility,
    questions: enQuestions,
    reviewQueue: enReviewQueue,
    associateProfile: enAssociateProfile,
    templates: enTemplates,
    iqQuestions: enIqQuestions,
    analytics: enAnalytics,
    auditLogs: enAuditLogs,
  },
} as const;

// ============================================================================
// STORAGE KEYS
// ============================================================================

export const LANGUAGE_STORAGE_KEY = 'i18nextLng';
export const RTL_STORAGE_KEY = 'i18n-rtl';

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Apply RTL/LTR direction to the document
 */
const applyDirection = (language: Language) => {
  const direction = getDirection(language);
  document.documentElement.dir = direction;
  document.documentElement.lang = language;

  // Add/remove RTL class for CSS styling
  if (direction === 'rtl') {
    document.body.classList.add('rtl');
    document.body.classList.remove('ltr');
  } else {
    document.body.classList.add('ltr');
    document.body.classList.remove('rtl');
  }

  // Store RTL preference
  localStorage.setItem(RTL_STORAGE_KEY, direction);
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS: 'common',
    ns: NAMESPACES as unknown as string[],
    fallbackLng: 'en',
    fallbackNS: 'common',
    debug: false,

    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },

    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span'],
    },
  });

// Apply RTL/LTR direction on language change
i18n.on('languageChanged', (lng) => {
  const language = lng as Language;
  applyDirection(language);

  // Dispatch custom event for components that need to react to language changes
  window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
});

// Set initial direction
const currentLang = (i18n.language || 'en') as Language;
applyDirection(currentLang);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Change the current language
 */
export const changeLanguage = async (language: Language): Promise<void> => {
  await i18n.changeLanguage(language);
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};

/**
 * Get the current language
 */
export const getCurrentLanguage = (): Language => {
  return (i18n.language || 'en') as Language;
};

/**
 * Get all available languages
 */
export const getAvailableLanguages = (): Language[] => {
  return Object.keys(LANGUAGES) as Language[];
};

/**
 * Format a number according to the current locale
 */
export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  const locale = getLocale(getCurrentLanguage());
  return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * Format a date according to the current locale
 */
export const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  const locale = getLocale(getCurrentLanguage());
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

/**
 * Format currency according to the current locale
 */
export const formatCurrency = (value: number, currency: string = 'USD'): string => {
  const locale = getLocale(getCurrentLanguage());
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
};

export default i18n;
